import { defineComponent as _defineComponent } from 'vue'
import { computed, useStore, ref, useContext, useRoute, onMounted, nextTick, watch } from '@nuxtjs/composition-api';
import SignUpSocialButtons from './SignUpSocialButtons/SignUpSocialButtons.vue';
import SignUpForm from './SignUpForm/SignUpForm.vue';
import { NUMBER_OF_USERS } from './constants';
import ModalBlack from './ModalBlack/ModalBlack.vue';
import { SIGN_UP_MODAL } from '@/constants/dynamicModals';
import type { RootState } from '~/types/store';
import CurrentTitleAutocomplete from '@/components/common/CurrentTitleAutocomplete/CurrentTitleAutocomplete.vue';
import Modal from '@/components/common/Modal/Modal.vue';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { COOKIE_NAME_SAVED_JOB_TITLE } from '@/components/common/constants';
import FadeTransition from '@/components/common/Transitions/FadeTransition.vue';
import {
  isUserInsideExternalApp as isUserInsideExternalAppUtil,
  isSalesGptReferrer,
} from '~/utils/externalRedirections';
import { escapeIterableQueryParam } from '~/components/utils/core/url';


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpModal',
  setup(__props) {

const { $pages, $device, $analytics, $modal, $cookies } = useContext();
const route = useRoute();
const store = useStore<RootState>();

const isUserInsideExternalApp = ref(false);
const jobTitle = ref('');
const isAdditionalQuestionOpened = ref(false);
const linkToOAuthPage = ref<string | undefined>(undefined);
const additionalQuestion = ref<HTMLElement | null>(null);

const promo = computed(() => store.state.promo);
const referer = computed(() => (route.value.query?.refferer || route.value.query?.referer) as string);
const isSalesGptReferer = computed(
  () => isSalesGptReferrer(process.client ? document.referrer : '') || referer.value?.includes('search'),
);

/**
 * We show additional question about job title only on /login-external before sign up
 */
const withAdditionalQuestion = computed(() => {
  return (
    [$pages.LOGIN_EXTERNAL_PAGE.name].includes(route.value.name ?? '') &&
    !isUserInsideExternalApp.value &&
    isSalesGptReferer.value
  );
});

/**
 * Modal has different appearance for: users from salesgpt landing,
 * from connections and universities mails.
 * Used params:
 * - invite_type
 * - signup_company_name
 * - signup_university_name
 * - signup_invitee_name
 * - signup_description - if we want to replace text fully
 * - signup_title - if we want to replace title fully
 */
const type = computed(() => {
  if (isSalesGptReferer.value) return 'salesgpt';
  if (route.value.query.invite_type === 'connection') return 'connection';
  if (route.value.query.invite_type === 'university') return 'university';
  return 'default';
});

const loginParams = computed(() => ({
  ...(promo.value && { promo: promo.value }),
  auth_source: 'sign-up-modal',
}));

const escapedUniversityName = computed(() =>
  escapeIterableQueryParam(route.value.query.signup_university_name as string),
);
const escapedCompanyName = computed(() => escapeIterableQueryParam(route.value.query.signup_company_name as string));

function setIsUserInsideExternalApp() {
  if (process.server) return;
  isUserInsideExternalApp.value = isUserInsideExternalAppUtil(
    document.referrer,
    navigator.userAgent,
    $device.isWebView,
  );
}

onMounted(() => {
  if (route.value.query.dynamic_modal !== 'sign-up') {
    $modal.hide(SIGN_UP_MODAL);
    return;
  }

  setIsUserInsideExternalApp();
});

watch(
  () => route.value.query.dynamic_modal,
  () => {
    setIsUserInsideExternalApp();
  },
);

function onSocialLogin(provider: 'google' | 'linkedin', link?: string) {
  if (withAdditionalQuestion.value) {
    linkToOAuthPage.value = link;
    isAdditionalQuestionOpened.value = true;
  }

  $analytics?.logEvent(`global__war-room-sign-up-modal__${provider}-button__click`);
}

function navigateToSignIn() {
  $analytics?.logEvent('global__war-room-sign-up-modal__login-link__click');
  $modal.showWithRoute('SIGN_IN_MODAL');
}

function handleJobTitleChanged(title: string) {
  jobTitle.value = title;
}

function handleAdditionalAnswerReplied() {
  $analytics?.logEvent('global__war-room-sign-up-modal__additional-answer__submit');
  $cookies?.set(COOKIE_NAME_SAVED_JOB_TITLE, jobTitle.value, { maxAge: 60 * 60 * 24, path: '/' }); // 1 day
  nextTick(() => {
    if (linkToOAuthPage.value) window.location.assign(linkToOAuthPage.value);
  });
}

watch(isAdditionalQuestionOpened, newVal => {
  if (newVal) {
    // Set focus after the animation to prevent the keyboard from popping up
    setTimeout(() => {
      additionalQuestion.value?.querySelector('input')?.focus();
    }, 500);
  }
});

return { __sfc: true,$pages, $device, $analytics, $modal, $cookies, route, store, isUserInsideExternalApp, jobTitle, isAdditionalQuestionOpened, linkToOAuthPage, additionalQuestion, promo, referer, isSalesGptReferer, withAdditionalQuestion, type, loginParams, escapedUniversityName, escapedCompanyName, setIsUserInsideExternalApp, onSocialLogin, navigateToSignIn, handleJobTitleChanged, handleAdditionalAnswerReplied, SignUpSocialButtons, SignUpForm, NUMBER_OF_USERS, ModalBlack, SIGN_UP_MODAL, CurrentTitleAutocomplete, Modal, BravadoButtonV3, FadeTransition }
}

})